/// <amd-module name="Build/Run/Components/CheckStatus" />
import * as React from "react";

import { CheckReference } from "Build/Client/Contracts/DataProvider";
import { CheckIssueType, CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import * as Constants from "Build/Run/Constants";
import * as Resources from "Build/Run/Resources";

import { BaseStatus, IBaseStatusOptions } from "Build/Run/Components/BaseStatus";

import { equals, format } from "VSS/Core/Util/String";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponentContext } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { css } from "VSSUI/Util";

interface ICheckStatusProps extends IBaseStatusOptions {
    checks: CheckReference[];
    rejectedChecks: number;
    showIssues?: Boolean;
}

export function CheckStatus(props: ICheckStatusProps) {
    const context = useComponentContext();
    let isBypassTaskChecksEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableBypassTaskChecks", false);
    let isBypassApprovalsEnabled = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableBypassApprovals", false);
    let isBypassFeatureEnabled = isBypassTaskChecksEnabled || isBypassApprovalsEnabled;

    let statusText: string | null;
    let extendedStatusText: string | null = null;

    if (!isBypassFeatureEnabled) {
        const text = getCompletedStatusText(props.checks, props.rejectedChecks);
        statusText = text;
    } else {
        const text = getInProgressStatusText(props.checks, props.rejectedChecks);
        statusText = text[0];
        extendedStatusText = text[1];
    }
    const warningStatus = statusText === Resources.PermissionNeeded;

    let checkIssueType = undefined;
    let checkIssueMessage = undefined;

    if (!!props.showIssues) {
        const checkIssues = props.checks.filter(x => x.issue != null);

        if (checkIssues.length > 0) {
            checkIssueType = checkIssues[0].issue.type;
            checkIssueMessage = checkIssues[0].issue.message;
        }
    }
    const checksComplianceWarning: JSX.Element = (
        <div className="summary-checks-issue-icon">
            {!!props.showIssues && !!checkIssueType && (
                <Icon
                    className={css(checkIssueType === CheckIssueType.Warning && "warning", checkIssueType === CheckIssueType.Error && "error")}
                    iconName={checkIssueType === CheckIssueType.Error ? "ErrorBadge" : "Warning"}
                    tooltipProps={{ text: checkIssueMessage }}
                />
            )}
        </div>
    );

    return (
        <BaseStatus
            text={statusText}
            extendedText={extendedStatusText}
            item={!!checkIssueType ? checksComplianceWarning : undefined}
            rejectedChecks={props.rejectedChecks > 0}
            onClick={props.onClick}
            iconName="WaitlistConfirm"
            onData={props.onData}
            warningStatus={warningStatus}
        />
    );
}

interface IManualValidationStatusProps extends IBaseStatusOptions {
    checks: CheckReference[];
    rejectedManualValidations: number;
}

export function ManualValidationStatus(props: IManualValidationStatusProps) {
    const text = getStatusTextForManualValidaton(props.checks, props.rejectedManualValidations);
    return (
        <BaseStatus
            text={text}
            rejectedChecks={props.rejectedManualValidations > 0}
            onClick={props.onClick}
            iconName="WaitlistConfirm"
            onData={props.onData}
        />
    );
}

function getCompletedStatusText(checks: CheckReference[], rejectedChecks: number): string | null {
    if (checks.some(check => equals(check.typeId, Constants.AuthorizationCheckTypeIdString, true) && check.status !== CheckRunStatus.Approved)) {
        return Resources.PermissionNeeded;
    }

    const onlyHasManualValidations = checks.length === checks.filter(x => equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true)).length;
    if (checks.length == 0 || onlyHasManualValidations) {
        return null;
    }

    const totalChecks = checks.filter(x => !equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true)).length;
    const passedChecks = checks.filter(
        x => x.status === CheckRunStatus.Approved && !equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true)
    ).length;

    if (totalChecks === 1 && passedChecks === 1) {
        return Resources.OneCheckPassed;
    }

    if (rejectedChecks > 0) {
        return format(Resources.ChecksFailedInfo, rejectedChecks === totalChecks ? totalChecks : rejectedChecks + "/" + totalChecks);
    }

    return format(Resources.ChecksStatusInfo, passedChecks === totalChecks ? totalChecks : passedChecks + "/" + totalChecks);
}

function getInProgressStatusText(checks: CheckReference[], rejectedChecks: number): [string | null, string | null] {
    if (checks.some(check => equals(check.typeId, Constants.AuthorizationCheckTypeIdString, true) && check.status !== CheckRunStatus.Approved)) {
        return [Resources.PermissionNeeded, null];
    }

    const onlyHasManualValidations = checks.length === checks.filter(x => equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true)).length;
    if (checks.length == 0 || onlyHasManualValidations) {
        return [null, null];
    }

    const checksWithoutManualValidation = checks.filter(x => !equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true));
    const totalChecks = checksWithoutManualValidation.length;

    if (rejectedChecks > 0) {
        return [format(Resources.ChecksFailedInfo, rejectedChecks === totalChecks ? totalChecks : rejectedChecks + "/" + totalChecks), null];
    }

    const bypassedChecks = checksWithoutManualValidation.filter(x => x.status === CheckRunStatus.Bypassed).length;
    const skippedChecksText = bypassedChecks === 1 ? Resources.OneCheckBypassed : format(Resources.MultipleChecksBypassed, bypassedChecks);
    const inProgressChecks = checksWithoutManualValidation.filter(x => !(x.status & (CheckRunStatus.Completed | CheckRunStatus.Queued))).length;
    const inProgressChecksText = inProgressChecks === 1 ? Resources.OneCheckInProgress : format(Resources.MultipleChecksInProgress, inProgressChecks);

    if (inProgressChecks > 0) {
        return [inProgressChecksText, bypassedChecks > 0 ? skippedChecksText : null];
    }

    if (bypassedChecks > 0) {
        return [skippedChecksText, null];
    }

    const approvedChecks = checksWithoutManualValidation.filter(x => x.status === CheckRunStatus.Approved).length;

    return [format(Resources.ChecksStatusInfo, approvedChecks), null];
}

function getStatusTextForManualValidaton(checks: CheckReference[], rejectedManualValidations: number): string | null {
    if (checks.length == 0) {
        return null;
    }

    const totalManualValidations = checks.filter(x => equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true)).length;

    if (totalManualValidations === 0) {
        return null;
    }

    const passedManualValidations = checks.filter(
        x => equals(x.typeId, Constants.ManualValidationTaskTypeIdString, true) && x.status === CheckRunStatus.Approved
    ).length;

    if (totalManualValidations === 1 && passedManualValidations === 1) {
        return Resources.OneManualValidationPassed;
    }

    if (rejectedManualValidations > 0) {
        return format(
            Resources.ManualValidationsFailedInfo,
            rejectedManualValidations === totalManualValidations ? totalManualValidations : rejectedManualValidations + "/" + totalManualValidations
        );
    }

    return format(
        Resources.ManualValidationsStatusInfo,
        passedManualValidations === totalManualValidations ? totalManualValidations : passedManualValidations + "/" + totalManualValidations
    );
}

/**
 * Custom hook to use the component context in functional components
 */
export function useComponentContext(): IVssComponentContext {
    return React.useContext(VssComponentContext);
}
